import { useMemo } from 'react';
import { type RouterOutputs, trpc } from '../lib/trpc';
import type { ClinicCapability } from '../types/clinic-access';

export type ThreadAccess = RouterOutputs['clinicAccess']['getThreadAccess'];

/**
 * What the current user may do with one conversation.
 *
 * Used to decide which controls exist, not whether an action is allowed — the server
 * checks that again on every call. The point is that a control someone cannot use should
 * not be a control they can press: a disabled button with a tooltip still reads as "you
 * are missing out", while an absent one reads as a page that was built for you.
 */
export function useClinicThreadAccess(conversationId: number | null | undefined) {
  const query = trpc.clinicAccess.getThreadAccess.useQuery(
    { conversationId: conversationId ?? 0 },
    { enabled: !!conversationId }
  );

  return useMemo(() => {
    const capabilities = new Set<ClinicCapability>(query.data?.capabilities ?? []);

    return {
      /** True only once the answer has actually arrived. */
      isLoading: query.isLoading,
      capabilities,
      can: (capability: ClinicCapability) => capabilities.has(capability),
      /**
       * Whether access came from the mailbox or from a grant on this conversation alone.
       * `conversationOnly` is what the explanatory line keys off.
       */
      source: query.data?.source ?? 'none',
      conversationOnly: query.data?.conversationOnly ?? false,
    };
  }, [query.data, query.isLoading]);
}

/**
 * Capabilities while the answer is still in flight.
 *
 * Everything is false until the server says otherwise. Optimistically showing a control
 * and removing it a moment later is worse than showing it slightly late — the flicker
 * reads as the interface changing its mind about who you are.
 */
export const NO_CLINIC_ACCESS = {
  isLoading: true,
  capabilities: new Set<ClinicCapability>(),
  can: () => false,
  source: 'none' as const,
  conversationOnly: false,
};
